import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Code, UserPlus, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Services = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, []);

  const recruitmentServices = services.filter(s => s.category === 'recruitment');
  const softwareServices = services.filter(s => s.category === 'software');

  return (
    <div className="min-h-screen bg-[#0a0f1a] pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32" data-testid="services-hero">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3184287/pexels-photo-3184287.jpeg"
            alt="Services"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1a] via-[#0a0f1a]/95 to-[#0a0f1a]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-[#FF6600] text-sm font-medium tracking-wider uppercase mb-4 block">
            Our Services
          </span>
          <h1 className="heading-1 mb-6">
            Comprehensive IT Solutions
          </h1>
          <p className="body-text max-w-2xl mx-auto">
            From talent acquisition to software development, we provide end-to-end 
            technology solutions that drive business growth.
          </p>
        </div>
      </section>

      {/* IT Recruitment Section */}
      <section className="section-padding" data-testid="recruitment-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Section Header */}
            <div className="lg:col-span-1">
              <div className="icon-container mb-6">
                <Users size={24} />
              </div>
              <h2 className="heading-2 mb-4">IT Recruitment Services</h2>
              <p className="text-slate-400 mb-6">
                We connect exceptional tech talent with leading companies. Our specialized 
                recruiters understand the technology landscape and find candidates who 
                fit both technically and culturally.
              </p>
              <Link to="/contact">
                <Button 
                  className="bg-[#0088CC] hover:bg-[#0099dd] text-white font-medium px-6 rounded-lg"
                  data-testid="recruitment-cta"
                >
                  Discuss Hiring Needs
                </Button>
              </Link>
            </div>

            {/* Service Cards */}
            <div className="lg:col-span-2 grid md:grid-cols-2 gap-6">
              <div className="service-card" data-testid="permanent-recruitment">
                <div className="icon-container mb-4">
                  <Users size={20} />
                </div>
                <h3 className="font-semibold text-white mb-2">Permanent Recruitment</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Find the perfect full-time employees for your tech teams. Our thorough 
                  vetting ensures quality matches.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#0088CC]" />
                    Technical assessments
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#0088CC]" />
                    Cultural fit evaluation
                  </li>
                </ul>
              </div>

              <div className="service-card" data-testid="contract-staffing">
                <div className="icon-container mb-4">
                  <Clock size={20} />
                </div>
                <h3 className="font-semibold text-white mb-2">Contract Staffing</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Flexible talent solutions for project-based needs. Scale your team up 
                  or down as required.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#0088CC]" />
                    Rapid deployment
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#0088CC]" />
                    Flexible terms
                  </li>
                </ul>
              </div>

              <div className="service-card" data-testid="executive-search">
                <div className="icon-container mb-4">
                  <UserPlus size={20} />
                </div>
                <h3 className="font-semibold text-white mb-2">Executive Search</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Find technology leaders who can drive your organization forward with 
                  vision and expertise.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#0088CC]" />
                    C-level recruitment
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#0088CC]" />
                    Confidential search
                  </li>
                </ul>
              </div>

              <div className="service-card" data-testid="rpo-services">
                <div className="icon-container mb-4">
                  <Users size={20} />
                </div>
                <h3 className="font-semibold text-white mb-2">RPO Services</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Outsource your entire recruitment process to our expert team for 
                  consistent, quality hiring.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#0088CC]" />
                    End-to-end management
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#0088CC]" />
                    Cost efficiency
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Software Development Section */}
      <section className="section-padding border-t border-[#1e293b]" data-testid="software-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Section Header */}
            <div className="lg:col-span-1 lg:order-2">
              <div className="icon-container-orange mb-6">
                <Code size={24} />
              </div>
              <h2 className="heading-2 mb-4">Software Development</h2>
              <p className="text-slate-400 mb-6">
                Custom software solutions built with cutting-edge technologies. From 
                web applications to enterprise systems, we deliver quality code that 
                scales with your business.
              </p>
              <Link to="/contact">
                <Button 
                  className="bg-[#0088CC] hover:bg-[#0099dd] text-white font-medium px-6 rounded-lg"
                  data-testid="software-cta"
                >
                  Start Your Project
                </Button>
              </Link>
            </div>

            {/* Service Cards */}
            <div className="lg:col-span-2 lg:order-1 grid md:grid-cols-2 gap-6">
              <div className="service-card" data-testid="web-applications">
                <div className="icon-container-orange mb-4">
                  <Code size={20} />
                </div>
                <h3 className="font-semibold text-white mb-2">Web Applications</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Modern, responsive web applications built with React, Angular, and Vue.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#FF6600]" />
                    Progressive Web Apps
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#FF6600]" />
                    SaaS Platforms
                  </li>
                </ul>
              </div>

              <div className="service-card" data-testid="mobile-apps">
                <div className="icon-container-orange mb-4">
                  <Code size={20} />
                </div>
                <h3 className="font-semibold text-white mb-2">Mobile Apps</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Native and cross-platform mobile applications for iOS and Android.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#FF6600]" />
                    React Native / Flutter
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#FF6600]" />
                    Native development
                  </li>
                </ul>
              </div>

              <div className="service-card" data-testid="cloud-solutions">
                <div className="icon-container-orange mb-4">
                  <Code size={20} />
                </div>
                <h3 className="font-semibold text-white mb-2">Cloud Solutions</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Cloud architecture and migration services on AWS, Azure, and GCP.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#FF6600]" />
                    Cloud migration
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#FF6600]" />
                    DevOps & CI/CD
                  </li>
                </ul>
              </div>

              <div className="service-card" data-testid="api-development">
                <div className="icon-container-orange mb-4">
                  <Code size={20} />
                </div>
                <h3 className="font-semibold text-white mb-2">API Development</h3>
                <p className="text-slate-500 text-sm mb-4">
                  RESTful and GraphQL APIs that power your applications.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#FF6600]" />
                    API design & docs
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={14} className="text-[#FF6600]" />
                    Third-party integrations
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding border-t border-[#1e293b]" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="heading-2 mb-6">
            Let's Build Something Great Together
          </h2>
          <p className="body-text mb-8">
            Ready to take your business to the next level? Contact us today.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-[#0088CC] hover:bg-[#0099dd] text-white font-medium px-8 py-6 text-base rounded-lg"
              data-testid="services-cta-btn"
            >
              Get a Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
